import {
  Badge,
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@elkaso-app/web-design';
import { useGetCrmSupplierContactQuotaByIdApi } from 'apis/crm-supplier-contact-quotas';
import React from 'react';

interface ViewQuotaModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotaId: number;
}

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <HStack justify='space-between' align='start'>
    <Text fontSize='sm' fontWeight='medium' color='gray.600' minW='120px'>
      {label}:
    </Text>
    <Box flex='1' textAlign='right'>
      {value}
    </Box>
  </HStack>
);

export const ViewQuotaModal: React.FC<ViewQuotaModalProps> = ({ isOpen, onClose, quotaId }) => {
  const { data: quotaDetails, isLoading } = useGetCrmSupplierContactQuotaByIdApi(quotaId, {
    enabled: isOpen && !!quotaId,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='lg'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Supplier Contact Quota Details</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {isLoading ? (
            <Box textAlign='center' py={8}>
              <Spinner size='lg' />
              <Text mt={4} color='gray.600'>
                Loading quota details...
              </Text>
            </Box>
          ) : quotaDetails ? (
            <VStack spacing={6} align='stretch'>
              {/* Supplier Information */}
              <Box p={4} bg='gray.50' borderRadius='md'>
                <Text
                  fontSize='sm'
                  fontWeight='semibold'
                  color='gray.700'
                  mb={3}
                  textTransform='uppercase'
                  letterSpacing='wide'>
                  Supplier Information
                </Text>
                <VStack spacing={3} align='stretch'>
                  <DetailRow label='Supplier ID' value={<Text fontWeight='semibold'>{quotaDetails.supplierId}</Text>} />
                  {quotaDetails.supplier && (
                    <>
                      <DetailRow
                        label='Name'
                        value={
                          <Text fontWeight='semibold' color='blue.600'>
                            {quotaDetails.supplier.name}
                          </Text>
                        }
                      />
                    </>
                  )}
                </VStack>
              </Box>

              {/* Quota Information */}
              <Box p={4} bg='blue.50' borderRadius='md'>
                <Text
                  fontSize='sm'
                  fontWeight='semibold'
                  color='blue.700'
                  mb={3}
                  textTransform='uppercase'
                  letterSpacing='wide'>
                  Quota Information
                </Text>
                <VStack spacing={3} align='stretch'>
                  <DetailRow
                    label='Total Quota'
                    value={
                      <Text fontWeight='bold' fontSize='lg' color='blue.600'>
                        {quotaDetails.quota}
                      </Text>
                    }
                  />
                  <DetailRow
                    label='Used'
                    value={
                      <Text fontWeight='bold' fontSize='lg' color='orange.600'>
                        {quotaDetails.used}
                      </Text>
                    }
                  />
                  <DetailRow
                    label='Remaining'
                    value={
                      <Text fontWeight='bold' fontSize='lg' color='green.600'>
                        {quotaDetails.remaining}
                      </Text>
                    }
                  />
                  <DetailRow
                    label='Usage'
                    value={
                      <Badge
                        colorScheme={
                          parseFloat(quotaDetails.usagePercentage) > 80
                            ? 'red'
                            : parseFloat(quotaDetails.usagePercentage) > 50
                            ? 'orange'
                            : 'green'
                        }
                        size='lg'>
                        {quotaDetails.usagePercentage}
                      </Badge>
                    }
                  />
                </VStack>
              </Box>

              {/* Timestamps */}
              <Box p={4} bg='gray.50' borderRadius='md'>
                <Text
                  fontSize='sm'
                  fontWeight='semibold'
                  color='gray.700'
                  mb={3}
                  textTransform='uppercase'
                  letterSpacing='wide'>
                  Timeline
                </Text>
                <VStack spacing={3} align='stretch'>
                  <DetailRow
                    label='Created At'
                    value={
                      <Text color='gray.700'>
                        {new Date(quotaDetails.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Text>
                    }
                  />
                  <DetailRow
                    label='Last Updated'
                    value={
                      <Text color='gray.700'>
                        {new Date(quotaDetails.updatedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Text>
                    }
                  />
                </VStack>
              </Box>
            </VStack>
          ) : (
            <Box textAlign='center' py={8}>
              <Text color='gray.600'>Quota details not found.</Text>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='blue' onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
